import { Box, Button, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { AiIcon, EducationIcon } from 'assets/icons/v2';
import { Filters } from 'components/common/Table/Filters';
import { NovaIcon } from 'components/ui/novaIcon';
import { Tooltip } from 'components/ui/tooltip';
import { useFeedbackSummaryGeneration } from 'hooks/api/backend/worksheets/useFeedbackSummaryGeneration';
import {
  useGetWorksheetFeedbackSummary,
  useGetWorksheetReviewStats,
} from 'hooks/api/backend/worksheets/useWorksheets';
import { useGetClassesByWorksheet } from 'hooks/api/backend/managament/getClasses';
import { useIsB2BTeacherOrAdmin } from 'hooks/utils/useIsB2BTeacherOrAdmin';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToastVariant } from 'types/Toast';
import { WorksheetClassSummaryStatCard } from './WorksheetClassSummaryStatCard';
import { WorksheetFeedbackSummaryContent } from './WorksheetFeedbackSummaryContent';
import { WorksheetFeedbackSummaryProgress } from './WorksheetFeedbackSummaryProgress';

type Props = {
  worksheetId: string | undefined;
};

const GENERATE_SUMMARY_LABEL = 'Zusammenfassung generieren';
const REFRESH_SUMMARY_LABEL = 'Zusammenfassung aktualisieren';

const formatSelfAssessmentAverage = (score: number | null | undefined): string => {
  if (score == null) return '—';
  return score.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

export const WorksheetClassSummary = ({ worksheetId }: Props): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  const classIdParam = searchParams.get('classId') ?? '';
  const [selectedClassId, setSelectedClassId] = useState<string>(classIdParam);
  const [generationActionLabel, setGenerationActionLabel] = useState(GENERATE_SUMMARY_LABEL);

  const { isB2BTeacherOrAdmin } = useIsB2BTeacherOrAdmin();
  const canUseClassFilter = isB2BTeacherOrAdmin === true;
  const summaryClassId = canUseClassFilter ? selectedClassId || undefined : undefined;

  const { show: showToast } = useNovaToast();
  const {
    generateSummary,
    isLoading: isSummaryGenerationActive,
    progress,
    progressMessage,
    error: generationError,
    reset: resetGeneration,
  } = useFeedbackSummaryGeneration(worksheetId, summaryClassId);

  const { data: classesByWorksheet } = useGetClassesByWorksheet(worksheetId || '');

  const { data: stats } = useGetWorksheetReviewStats(worksheetId, summaryClassId);

  const { data: feedbackSummary = null, isLoading: isFeedbackSummaryLoading } =
    useGetWorksheetFeedbackSummary(worksheetId, summaryClassId);

  useEffect(() => {
    if (!generationError) return;
    showToast({
      description: 'Die KI-Zusammenfassung konnte nicht erstellt werden.',
      variant: ToastVariant.ERROR,
    });
    resetGeneration();
  }, [generationError, resetGeneration, showToast]);

  const classOptions = useMemo(
    () =>
      classesByWorksheet?.classes
        .filter((item) => item.isConnectedToWorksheet)
        .map((item) => ({
          id: item.id,
          label: item.name,
        })) ?? [],
    [classesByWorksheet]
  );

  useEffect(() => {
    if (!canUseClassFilter) return;
    setSelectedClassId(classIdParam);
  }, [classIdParam, canUseClassFilter]);

  useEffect(() => {
    if (!canUseClassFilter) return;
    if (!classIdParam) return;
    if (classOptions.length === 0) return;
    const hasSelectedClass = classOptions.some((option) => option.id === classIdParam);
    if (!hasSelectedClass) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('classId');
      setSearchParams(nextParams);
    }
  }, [canUseClassFilter, classIdParam, classOptions, searchParams, setSearchParams]);

  const handleClassChange = (classId: string): void => {
    if (classId === classIdParam) return;
    const nextParams = new URLSearchParams(searchParams);
    if (classId) {
      nextParams.set('classId', classId);
    } else {
      nextParams.delete('classId');
    }
    setSearchParams(nextParams);
  };

  const handleGenerateSummary = (actionLabel: string = GENERATE_SUMMARY_LABEL): void => {
    if (!worksheetId) return;
    setGenerationActionLabel(actionLabel);
    generateSummary(
      worksheetId,
      canUseClassFilter && selectedClassId ? selectedClassId : undefined
    );
  };

  const completedParticipantsDisplay = useMemo(() => {
    if (!stats) return '—';
    if (stats.participantCount === 0) return '—';
    const showClassRatio = canUseClassFilter && selectedClassId;
    if (showClassRatio) {
      return `${stats.submittedCount}/${stats.participantCount}`;
    }
    return String(stats.submittedCount);
  }, [stats, canUseClassFilter, selectedClassId]);

  const submittedAnswersCount = stats?.submittedCount ?? 0;
  const hasEnoughSubmittedAnswersForSummary = submittedAnswersCount >= 5;
  const summaryUnavailableReason = useMemo(() => {
    if (!hasEnoughSubmittedAnswersForSummary) {
      return `Mindestens 5 abgegebene Antworten erforderlich (${submittedAnswersCount}/5).`;
    }
    return null;
  }, [hasEnoughSubmittedAnswersForSummary, submittedAnswersCount]);

  const isGenerateSummaryDisabled = !!summaryUnavailableReason || isSummaryGenerationActive;

  const renderGenerationProgress = (): ReactElement | null => {
    if (!isSummaryGenerationActive) return null;

    return (
      <WorksheetFeedbackSummaryProgress
        actionLabel={generationActionLabel}
        progress={progress}
        progressMessage={progressMessage}
      />
    );
  };

  const renderFeedbackSummarySection = (): ReactElement => {
    if (isFeedbackSummaryLoading) {
      return (
        <Text textStyle="body.s.regular" color="text.tertiary">
          Wird geladen...
        </Text>
      );
    }

    if (feedbackSummary) {
      return (
        <VStack align="stretch" gap="m" width="100%">
          {renderGenerationProgress()}
          <WorksheetFeedbackSummaryContent
            summary={feedbackSummary}
            summaryClassId={summaryClassId}
            onRefresh={() => handleGenerateSummary(REFRESH_SUMMARY_LABEL)}
            isRefreshing={isSummaryGenerationActive}
          />
        </VStack>
      );
    }

    if (isSummaryGenerationActive) {
      return (
        <WorksheetFeedbackSummaryProgress
          actionLabel={generationActionLabel}
          progress={progress}
          progressMessage={progressMessage}
        />
      );
    }

    return (
      <Tooltip
        disabled={!summaryUnavailableReason}
        content={
          <Text textStyle="body.s.regular" color="text.secondary">
            {summaryUnavailableReason}
          </Text>
        }
        showArrow
      >
        <Box as="span" display="inline-flex" alignSelf="flex-start">
          <Button
            variant="secondaryOutline"
            size="m"
            onClick={() => handleGenerateSummary(GENERATE_SUMMARY_LABEL)}
            disabled={isGenerateSummaryDisabled}
            borderRadius="l"
            px="m"
            py="s"
            gap="2xs"
          >
            <NovaIcon icon={AiIcon} size="s" aria-hidden />
            {GENERATE_SUMMARY_LABEL}
          </Button>
        </Box>
      </Tooltip>
    );
  };

  return (
    <VStack width="100%" align="stretch" gap="3xl" pt="l">
      <VStack align="stretch" gap="l" width="100%">
        <Text textStyle="body.m.medium" color="text.primary">
          Zusammenfassung
        </Text>
        {canUseClassFilter && (
          <Box maxW="277px" width="100%">
            <Filters
              filters={[
                [
                  {
                    id: 'class',
                    placeholder: 'Alle Teilnehmenden',
                    value: selectedClassId,
                    options: classOptions,
                    onChange: handleClassChange,
                    icon: <EducationIcon color="icon.accent" />,
                    showPlaceholderAsFirstOption: true,
                    disabled: classOptions.length === 0,
                  },
                ],
              ]}
            />
          </Box>
        )}
      </VStack>

      <VStack align="stretch" gap="m" width="100%">
        <Text textStyle="body.m.medium" color="text.primary">
          Übersicht
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="m" width="100%">
          <WorksheetClassSummaryStatCard
            label="Anzahl Teilnehmende"
            value={stats && stats.participantCount > 0 ? String(stats.participantCount) : '—'}
          />
          <WorksheetClassSummaryStatCard
            label="Abgeschlossene Teilnehmende"
            value={completedParticipantsDisplay}
          />
          <WorksheetClassSummaryStatCard
            label="Durchschnittliche Selbsteinschätzung"
            value={formatSelfAssessmentAverage(stats?.averageSelfAssessmentScore)}
          />
        </SimpleGrid>
      </VStack>

      <Box width="100%" borderTop="1px solid" borderColor="border.accent" />

      <VStack align="stretch" gap="m" width="100%">
        <VStack align="stretch" gap="2xs">
          <Text textStyle="body.m.medium" color="text.primary">
            KI-Arbeitsblatt-Zusammenfassung
          </Text>
          <Text textStyle="body.s.regular" color="text.primary">
            KI-generierte Übersicht zur Leistung der Auszubildenden
          </Text>
        </VStack>

        {renderFeedbackSummarySection()}
      </VStack>
    </VStack>
  );
};
